import { Fragment, ReactElement, useEffect, useState } from "react";
import { T } from "react-translator-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, List, ListItem, Typography } from "@mui/material";
import appServices from "../../../services/app-services/appServices";
import dayjs from "../../../util/dayjs";
import { adjustUserInfo } from "../../../util/MultiMenu";
import GCBackButton from "../../../components/GreenCity/GCBackButton";
import LoadingModal from "../../../components/LoadingModal";
import ConfirmationModal from "../../../components/Models/GreenCity/ConfirmationModal";
import MessageModal from "../../../components/Models/GreenCity/MessageModal";
import MultiMenu from "../../../components/MultiMenu";
import TwoButtonModal from "../../../components/TwoButtonModal";
import UserCard from "../../../components/Models/GreenCity/UserCard";
import { ErrorMessageModal } from "../../../components/ErrorMessageModal";
import { styles } from "../../styles";

function GroupMembers(): ReactElement {
  const defaultImage = require("../../../assets/images/default.png");

  const navigate = useNavigate();
  const groupInfo = useSelector((state: any) => state.gcGroup);

  const [members, setMembers] = useState<any[]>([]);
  const [member, setMember] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [deleteId, setId] = useState<any>("");
  const [message, setMessage] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);

  const handleDelete = (id: any) => {
    setId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    apiCall();
    // eslint-disable-next-line
  }, []);

  const apiCall = async () => {
    const res = await appServices.getMemberPendingList(groupInfo?.id);
    setMembers(res?.member_group_users);
  };

  const handleApprove = async (member: any) => {
    setLoading(true);

    const body = {
      role: "member",
      status: groupInfo?.isPaid ? "unpaid_approved" : "approved",
    }
    try {
      await appServices.updateMemberGroupUsers(member?.id, body);
      setMessage(T("member_list_approved"));
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message ?? [T("api_unknown_error")])
      setShowErrorModal(true)
    } finally {
      setLoading(false);
      apiCall();
    }
  };

  const handleDeleteMember = async (id: any) => {
    handleClose();
    setLoading(true);
    try {
      const body = {
        role: "denied",
        status: "denied",
      };
      await appServices.updateMemberGroupUsers(deleteId, body);
      apiCall();
      setMessage(T("member_list_denied"));
      return true
    } catch (error: any) {
      console.error(error)
      setErrorMessage(error?.response?.data?.message ?? [T("api_unknown_error")])
      setShowErrorModal(true)
      return false
    } finally {
      setLoading(false);
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false)
    setErrorMessage([])
  }

  return (
    <div style={{ paddingTop: 100, marginLeft: 16 }}>
      <GCBackButton onClick={() => navigate(-1)} />
      <Box style={{ padding: 16 }}>
        <Typography style={{ fontWeight: "700" }}>
          <img
            src={groupInfo?.groupImage instanceof File ? URL.createObjectURL(groupInfo.groupImage) : defaultImage}
            alt={"user_img"}
            style={{
              ...styles.farmProfileIcon,
              height: 50,
              width: 50,
              margin: 0,
            }}
          />
          &nbsp;{groupInfo?.groupName}
        </Typography>
      </Box>
      <List>
        {members?.map((member: any) => (
          <Fragment key={member?.id}>
            <ListItem disableGutters>
              <UserCard
                userImage={member?.user?.image}
                userName={member?.user?.nickname || member?.user?.name}
                role={dayjs(member?.created_at).format("YYYY年M月D日(ddd)")}
                showApproveIcon={member.status === "pending"}
                onApprove={() => setMember(member)}
                onDelete={() => handleDelete(member.id)}
                showMoreOptionsIcon={false}
              />
              <MultiMenu
                targetUser={adjustUserInfo(member.user, groupInfo)}
                menuType={"waiting_approval_member"}
              />
            </ListItem>
            <Divider component="li" /> {/* Adds a divider between each user */}
          </Fragment>
        ))}
      </List>
      <LoadingModal visible={loading} handleClose={() => {}} />
      <ConfirmationModal
        open={member ? true : false}
        message={
          <span style={{ display: "block", textAlign: 'center' }}>
            {T("member_list_confirm_approving_request")}
          </span>
        }
        onClose={() => setMember(undefined)}
        onContinue={() => {
          handleApprove(member);
          setMember(undefined);
        }}
      />
      <TwoButtonModal
        open={open}
        message={`${T("member_list_confirm_denying_request")}`}
        onClose={handleClose}
        onContinue={() => handleDeleteMember(member)}
        noText={T("back_kanji")}
      />
      <MessageModal
        open={message.length > 0}
        message={message}
        onClose={() => setMessage("")}
      />
      <ErrorMessageModal
        open={showErrorModal}
        messages={errorMessage}
        onClose={() => closeErrorModal()}
      />
    </div>
  );
}

export default GroupMembers;
